import React, { useState, useRef, useEffect, useMemo } from "react";
import { apiCall } from "../../../../_services/apiCall";
import config from "../../../../config/config.json";

const KpiRequestModal = ({ show, onHide, defaultType = "KPI", kpiId = null, kpiTitle = null, onSuccess }) => {
    const [form, setForm] = useState({ title: "", description: "", request_type: defaultType });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);
    const backdropRef = useRef(null);

    // Unit-specific state for custom requests
    const [requestedUnit, setRequestedUnit] = useState("");
    const [requestedUnitName, setRequestedUnitName] = useState("");

    useEffect(() => {
        setForm({ title: "", description: "", request_type: defaultType });
        setError("");
        setSuccess(false);
        setSubmitting(false);
        setRequestedUnit("");
        setRequestedUnitName("");
    }, [show, defaultType]);

    if (!show) return null;

    const handleChange = (key, val) => {
        setForm((p) => ({ ...p, [key]: val }));
        setError("");
    };

    const handleSubmit = async () => {
        if (defaultType === "UNIT") {
            if (!requestedUnit.trim()) { setError("Unit symbol is required."); return; }
            if (!requestedUnitName.trim()) { setError("Unit name is required."); return; }
            setSubmitting(true);
            setError("");
            try {
                const unitTitle = `${requestedUnitName.trim()} (${requestedUnit.trim()})`;
                const { isSuccess, data } = await apiCall(
                    `${config.POSTLOGIN_API_URL_COMPANY}quantative-data/kpi-request`,
                    {},
                    {
                        title: unitTitle,
                        description: form.description.trim(),
                        request_type: "UNIT",
                        kpi_id: kpiId,
                        unit_id: null,
                        unit: requestedUnit.trim(),
                        unit_name: requestedUnitName.trim(),
                        factor: null,
                    },
                    "POST", false, false
                );
                if (isSuccess) {
                    setSuccess(true);
                    onSuccess?.();
                    setTimeout(() => { onHide(); }, 1600);
                } else {
                    setError(data?.message || "Submission failed. Please try again.");
                }
            } catch (e) {
                setError("Something went wrong. Please try again.");
            } finally {
                setSubmitting(false);
            }
            return;
        }

        if (!form.title.trim()) { setError("Title is required."); return; }
        setSubmitting(true);
        setError("");
        try {
            const { isSuccess, data } = await apiCall(
                `${config.POSTLOGIN_API_URL_COMPANY}quantative-data/kpi-request`,
                {},
                { 
                    title: form.title.trim(), 
                    description: form.description.trim(), 
                    request_type: form.request_type,
                    kpi_id: kpiId
                },
                "POST",
                false,
                false
            );
            if (isSuccess) {
                setSuccess(true);
                onSuccess?.();
                setTimeout(() => { onHide(); }, 1600);
            } else {
                setError(data?.message || "Submission failed. Please try again.");
            }
        } catch (e) {
            setError("Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleBackdropClick = (e) => {
        if (e.target === backdropRef.current) onHide();
    };

    return (
        <div
            ref={backdropRef}
            onClick={handleBackdropClick}
            style={{
                position: "fixed", inset: 0,
                background: "rgba(15,23,42,0.45)",
                zIndex: 9999,
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: 20,
            }}
        >
            <div style={{
                background: "var(--surface, #fff)",
                borderRadius: 14,
                width: "100%", maxWidth: 480,
                boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
                overflow: "hidden",
                fontFamily: "var(--font-sans)",
            }}>
                {/* Header */}
                <div style={{
                    padding: "18px 24px 16px",
                    borderBottom: "1px solid var(--rule, #e5e7eb)",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                }}>
                    <div>
                        <div style={{ fontSize: 16, fontWeight: 800, color: "var(--ink, #0f172a)", letterSpacing: "-0.02em" }}>
                            {defaultType === "UNIT" ? "Add Unit" : "Request KPI"}
                        </div>
                        <div style={{ fontSize: 12, color: "var(--ink-4, #94a3b8)", marginTop: 2 }}>
                            {defaultType === "UNIT" ? "Select a unit and enter its conversion factor" : "Submit a request for a new KPI to be added"}
                        </div>
                    </div>
                    <button
                        onClick={onHide}
                        style={{
                            border: "none", background: "transparent", cursor: "pointer",
                            fontSize: 20, color: "var(--ink-4, #94a3b8)", lineHeight: 1,
                            padding: "2px 4px", borderRadius: 6,
                        }}
                    >×</button>
                </div>

                {/* Body */}
                <div style={{ padding: "20px 24px 8px", display: "flex", flexDirection: "column", gap: 16 }}>

                    {/* Request Type (KPI/TAG only) */}
                    {defaultType !== "UNIT" ? (
                        <div>
                            <label style={{
                                display: "block", fontSize: 11.5, fontWeight: 700,
                                color: "var(--ink-3, #475569)", marginBottom: 6,
                                textTransform: "uppercase", letterSpacing: "0.06em",
                            }}>
                                Request Type <span style={{ color: "var(--red, #dc2626)" }}>*</span>
                            </label>
                            <select
                                value={form.request_type}
                                onChange={(e) => handleChange("request_type", e.target.value)}
                                style={{
                                    width: "100%", height: 38,
                                    padding: "0 12px",
                                    fontSize: 13, fontFamily: "var(--font-sans)",
                                    border: "1.5px solid var(--rule, #e2e8f0)",
                                    borderRadius: 8,
                                    background: "var(--surface, #fff)",
                                    color: "var(--ink, #0f172a)",
                                    outline: "none",
                                    cursor: "pointer",
                                }}
                                onFocus={(e) => { e.target.style.borderColor = "var(--accent, #3f88a5)"; }}
                                onBlur={(e) => { e.target.style.borderColor = "var(--rule, #e2e8f0)"; }}
                            >
                                <option value="KPI">KPI</option>
                            </select>
                        </div>
                    ) : null}

                    {/* UNIT mode: Unit dropdown + Factor row */}
                    {defaultType === "UNIT" ? (
                        <>
                            {/* KPI context chip */}
                            {kpiTitle && (
                                <div style={{
                                    background: "#f0f9ff", border: "1px solid #bae6fd",
                                    borderRadius: 8, padding: "8px 12px",
                                    fontSize: 12, color: "#0369a1",
                                    display: "flex", alignItems: "center", gap: 6,
                                }}>
                                    <span style={{ fontSize: 11 }}>📌</span>
                                    Adding unit for: <strong style={{ marginLeft: 2 }}>{kpiTitle}</strong>
                                </div>
                            )}

                            {/* Unit symbol text input */}
                            <div>
                                <label style={{
                                    display: "block", fontSize: 11.5, fontWeight: 700,
                                    color: "var(--ink-3, #475569)", marginBottom: 6,
                                    textTransform: "uppercase", letterSpacing: "0.06em",
                                }}>
                                    Unit Symbol / Abbreviation <span style={{ color: "var(--red, #dc2626)" }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="e.g. GW"
                                    value={requestedUnit}
                                    onChange={(e) => { setRequestedUnit(e.target.value); setError(""); }}
                                    style={{
                                        width: "100%", height: 38,
                                        padding: "0 12px",
                                        fontSize: 13, fontFamily: "var(--font-sans)",
                                        border: `1.5px solid ${error && !requestedUnit.trim() ? "var(--red, #dc2626)" : "var(--rule, #e2e8f0)"}`,
                                        borderRadius: 8,
                                        background: "var(--surface, #fff)",
                                        color: "var(--ink, #0f172a)",
                                        outline: "none",
                                        boxSizing: "border-box",
                                    }}
                                    onFocus={(e) => { e.target.style.borderColor = "var(--accent, #3f88a5)"; }}
                                    onBlur={(e) => { e.target.style.borderColor = "var(--rule, #e2e8f0)"; }}
                                />
                            </div>

                            {/* Unit name text input */}
                            <div>
                                <label style={{
                                    display: "block", fontSize: 11.5, fontWeight: 700,
                                    color: "var(--ink-3, #475569)", marginBottom: 6,
                                    textTransform: "uppercase", letterSpacing: "0.06em",
                                }}>
                                    Unit Name <span style={{ color: "var(--red, #dc2626)" }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="e.g. gigawatt"
                                    value={requestedUnitName}
                                    onChange={(e) => { setRequestedUnitName(e.target.value); setError(""); }}
                                    style={{
                                        width: "100%", height: 38,
                                        padding: "0 12px",
                                        fontSize: 13, fontFamily: "var(--font-sans)",
                                        border: `1.5px solid ${error && !requestedUnitName.trim() ? "var(--red, #dc2626)" : "var(--rule, #e2e8f0)"}`,
                                        borderRadius: 8,
                                        background: "var(--surface, #fff)",
                                        color: "var(--ink, #0f172a)",
                                        outline: "none",
                                        boxSizing: "border-box",
                                    }}
                                    onFocus={(e) => { e.target.style.borderColor = "var(--accent, #3f88a5)"; }}
                                    onBlur={(e) => { e.target.style.borderColor = "var(--rule, #e2e8f0)"; }}
                                />
                            </div>

                        </>
                    ) : (
                        /* KPI/TAG mode: Title text input */
                        <div>
                            <label style={{
                                display: "block", fontSize: 11.5, fontWeight: 700,
                                color: "var(--ink-3, #475569)", marginBottom: 6,
                                textTransform: "uppercase", letterSpacing: "0.06em",
                            }}>
                                Title <span style={{ color: "var(--red, #dc2626)" }}>*</span>
                            </label>
                            <input
                                type="text"
                                value={form.title}
                                onChange={(e) => handleChange("title", e.target.value)}
                                placeholder="e.g. Total Water Consumption"
                                maxLength={200}
                                style={{
                                    width: "100%", height: 38,
                                    padding: "0 12px",
                                    fontSize: 13, fontFamily: "var(--font-sans)",
                                    border: `1.5px solid ${error && !form.title.trim() ? "var(--red, #dc2626)" : "var(--rule, #e2e8f0)"}`,
                                    borderRadius: 8,
                                    background: "var(--surface, #fff)",
                                    color: "var(--ink, #0f172a)",
                                    outline: "none",
                                    boxSizing: "border-box",
                                }}
                                onFocus={(e) => { if (!error) e.target.style.borderColor = "var(--accent, #3f88a5)"; }}
                                onBlur={(e) => { if (!error || form.title.trim()) e.target.style.borderColor = "var(--rule, #e2e8f0)"; }}
                            />
                        </div>
                    )}

                    {/* Description */}
                    <div>
                        <label style={{
                            display: "block", fontSize: 11.5, fontWeight: 700,
                            color: "var(--ink-3, #475569)", marginBottom: 6,
                            textTransform: "uppercase", letterSpacing: "0.06em",
                        }}>
                            Description <span style={{ fontSize: 11, fontWeight: 400, color: "var(--ink-4, #94a3b8)", textTransform: "none" }}>(Optional)</span>
                        </label>
                        <textarea
                            value={form.description}
                            onChange={(e) => handleChange("description", e.target.value)}
                            placeholder={
                                defaultType === "UNIT"
                                    ? "Any additional notes about this unit request…"
                                    : "Describe the KPI, its unit, how it should be measured…"
                            }
                            rows={3}
                            maxLength={1000}
                            style={{
                                width: "100%",
                                padding: "10px 12px",
                                fontSize: 13, fontFamily: "var(--font-sans)",
                                border: "1.5px solid var(--rule, #e2e8f0)",
                                borderRadius: 8,
                                background: "var(--surface, #fff)",
                                color: "var(--ink, #0f172a)",
                                outline: "none",
                                resize: "vertical",
                                lineHeight: 1.55,
                                boxSizing: "border-box",
                            }}
                            onFocus={(e) => { e.target.style.borderColor = "var(--accent, #3f88a5)"; }}
                            onBlur={(e) => { e.target.style.borderColor = "var(--rule, #e2e8f0)"; }}
                        />
                        <div style={{ fontSize: 10.5, color: "var(--ink-4, #94a3b8)", textAlign: "right", marginTop: 4 }}>
                            {form.description.length}/1000
                        </div>
                    </div>

                    {/* Error */}
                    {error && (
                        <div style={{
                            fontSize: 12, color: "var(--red, #dc2626)",
                            background: "#fef2f2", border: "1px solid #fecaca",
                            borderRadius: 8, padding: "8px 12px",
                            fontFamily: "var(--font-sans)",
                        }}>
                            {error}
                        </div>
                    )}

                    {/* Success */}
                    {success && (
                        <div style={{
                            fontSize: 12, color: "#15803d",
                            background: "#f0fdf4", border: "1px solid #bbf7d0",
                            borderRadius: 8, padding: "8px 12px",
                            fontFamily: "var(--font-sans)",
                            display: "flex", alignItems: "center", gap: 6,
                        }}>
                            <span>✓</span> {defaultType === "UNIT" ? "Unit added successfully!" : "Request submitted successfully!"}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    padding: "16px 24px 20px",
                    display: "flex", justifyContent: "flex-end", gap: 10,
                }}>
                    <button
                        onClick={onHide}
                        disabled={submitting}
                        style={{
                            height: 36, padding: "0 18px",
                            border: "1.5px solid var(--rule, #e2e8f0)",
                            borderRadius: 8, background: "transparent",
                            color: "var(--ink-3, #475569)",
                            fontSize: 13, fontWeight: 600,
                            fontFamily: "var(--font-sans)",
                            cursor: submitting ? "not-allowed" : "pointer",
                            opacity: submitting ? 0.5 : 1,
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={submitting || success}
                        style={{
                            height: 36, padding: "0 20px",
                            border: "none",
                            borderRadius: 8,
                            background: success ? "#16a34a" : "var(--accent, #3f88a5)",
                            color: "#fff",
                            fontSize: 13, fontWeight: 700,
                            fontFamily: "var(--font-sans)",
                            cursor: submitting || success ? "not-allowed" : "pointer",
                            opacity: submitting ? 0.75 : 1,
                            display: "flex", alignItems: "center", gap: 7,
                            transition: "background 0.2s",
                        }}
                    >
                        {submitting && (
                            <div style={{
                                width: 13, height: 13,
                                border: "2px solid rgba(255,255,255,0.4)",
                                borderTopColor: "#fff",
                                borderRadius: "50%",
                                animation: "spin 0.7s linear infinite",
                            }} />
                        )}
                        {success ? "✓ Submitted" : submitting ? (defaultType === "UNIT" ? "Adding…" : "Submitting…") : (defaultType === "UNIT" ? "Add Unit" : "Submit Request")}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default KpiRequestModal;
